import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { updatePnlDropdowns, PnlLevel, fetchPnlYears } from '../pages/pnl/api';

function useStableRef<T>(initial: T) {
  const ref = React.useRef(initial);
  return ref;
}

interface CustomerDataFiltersContextType {
  // State
  years: string[];
  year: string;
  levels: PnlLevel[];
  loading: boolean;
  error: string;

  // Actions
  handleSelectChange: (levelId: number | string, value: string[]) => void;
  handleBatchSelect: (updates: Array<{ levelId: number | string; value: string[] }>) => void;
  handleYearChange: (newYear: string) => void;
  buildFiltersFromLevels: (currentLevels: PnlLevel[]) => Record<string, string[]>;
  getCurrentFilters: () => Record<string, string[]>;
}

const CustomerDataFiltersContext = createContext<CustomerDataFiltersContextType | null>(null);

export const useCustomerDataFilters = () => {
  const context = useContext(CustomerDataFiltersContext);
  if (!context) {
    throw new Error('useCustomerDataFilters must be used within CustomerDataFiltersProvider');
  }
  return context;
};

interface CustomerDataFiltersProviderProps {
  children: React.ReactNode;
  dropdownsApi?: (payload: {
    year: string;
    selection: Record<string, string[]>;
  }) => Promise<{ levels: PnlLevel[] }>;
}

export const CustomerDataFiltersProvider: React.FC<CustomerDataFiltersProviderProps> = ({ children, dropdownsApi }) => {
  const [years, setYears] = useState<string[]>([]);
  const [year, setYear] = useState<string>('');
  const [levels, setLevels] = useState<PnlLevel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  const didInitRef = useStableRef(false);

  const fetchLevels = useCallback(async (selectionMap?: Record<string, string[]>) => {
    if (!year) return;
    setLoading(true);
    setError('');
    try {
      const apiFn = dropdownsApi ?? updatePnlDropdowns;
      const data = await apiFn({ year, selection: selectionMap || {} });
      setLevels(Array.isArray(data?.levels) ? data.levels : []);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Something went wrong';
      setError(message);
      if (isForbidden) setLevels([]);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const buildFiltersFromLevels = useCallback((currentLevels: PnlLevel[]) => {
    const filters: Record<string, string[]> = {};
    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        filters[String(level.id)] = level.selected;
      }
    }
    return filters;
  }, []);

  const buildSelectionFromLevels = useCallback((currentLevels: PnlLevel[]) => {
    const map: Record<string, string[]> = {};
    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        map[String(level.id)] = level.selected;
      }
    }
    return map;
  }, []);

  const handleSelectChange = useCallback((levelId: number | string, value: string[]) => {
    const desc = (levels || [])
      .filter((l) => Boolean(l?.visible))
      .sort((a, b) => Number(b.id) - Number(a.id));
    const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
    const changedIdx = indexById[String(levelId)];

    const nextLevels = levels.map((lvl) => {
      const idx = indexById[String(lvl.id)];
      if (idx < changedIdx) {
        return lvl.id === levelId ? { ...lvl, selected: value.length > 0 ? value : null } : lvl;
      }
      if (idx === changedIdx) {
        return { ...lvl, selected: value.length > 0 ? value : null };
      }
      return { ...lvl, selected: null };
    });

    setLevels(nextLevels);
    const selection = buildSelectionFromLevels(nextLevels);
    if (value.length === 0) delete selection[String(levelId)];
    fetchLevels(selection);
  }, [levels, buildSelectionFromLevels, fetchLevels]);

  const handleYearChange = useCallback((newYear: string) => {
    if (!newYear) return;
    setYear(newYear);
  }, []);

  const handleBatchSelect = useCallback((updates: Array<{ levelId: number | string; value: string[] }>) => {
    if (!Array.isArray(updates) || updates.length === 0) return;

    const updatesById = Object.fromEntries(updates.map(u => [String(u.levelId), u.value]));

    const nextLevels = (levels || []).map((lvl) => {
      const updated = updatesById[String(lvl.id)];
      if (updated) {
        return { ...lvl, selected: updated };
      }
      return lvl;
    });

    setLevels(nextLevels);
    const selection = buildSelectionFromLevels(nextLevels);
    fetchLevels(selection);
  }, [levels, buildSelectionFromLevels, fetchLevels]);

  const getCurrentFilters = useCallback(() => {
    return buildFiltersFromLevels(levels);
  }, [levels, buildFiltersFromLevels]);

  // Initialize years and year on mount
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      try {
        const resp = await fetchPnlYears();
        const yrList = Array.isArray(resp?.years) ? resp.years : [];
        setYears(yrList);
        setYear(yrList[0] || '');
      } catch (e) {
        setYears([]);
        setYear('');
      }
    })();
  }, [didInitRef]);

  // Reset levels when year changes
  useEffect(() => {
    if (!year) return;
    setLevels([]);
    fetchLevels({});
  }, [year, fetchLevels]);

  const contextValue: CustomerDataFiltersContextType = {
    years,
    year,
    levels,
    loading,
    error,
    handleSelectChange,
    handleBatchSelect,
    handleYearChange,
    buildFiltersFromLevels,
    getCurrentFilters,
  };

  return (
    <CustomerDataFiltersContext.Provider value={contextValue}>
      {children}
    </CustomerDataFiltersContext.Provider>
  );
};
